"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth, useUser } from "@clerk/nextjs";
import { usePathname, useSearchParams } from "next/navigation";
import posthog from "posthog-js";
import type { CaptureResult } from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import type { ReactNode } from "react";
import { Suspense, useEffect, useRef } from "react";
import { usePostHogConsent } from "@/hooks/use-posthog-consent";
import { POSTHOG_BROWSER_PROXY_HOST, applyPostHogDebugQueryParam, getPostHogAppHost, getPostHogProjectKey, hasPostHogConsent, identifyPosthogUser, shouldFilterPostHogException } from "@/lib/posthog";
const POSTHOG_KEY = getPostHogProjectKey();
const POSTHOG_PII_DENYLIST = ["email", "name", "firstName", "lastName", "phone", "document", "cpf", "cnpj", "address", "image"];
function getEventProperty(event: unknown, key: string) {
  if (!event || typeof event !== "object" || !("properties" in event)) {
    return undefined;
  }
  const properties = event.properties;
  if (!properties || typeof properties !== "object" || !(key in properties)) {
    return undefined;
  }
  return properties[key as keyof typeof properties];
}
function beforeSendPostHogEvent(event: CaptureResult | null) {
  if (!event) {
    return event;
  }
  const eventName = event && typeof event === "object" && "event" in event ? event.event : undefined;
  if (eventName !== "$exception") {
    return event;
  }
  const message = getEventProperty(event, "$exception_message");
  if (typeof message === "string" && shouldFilterPostHogException(message)) {
    return null;
  }
  return event;
}
function PostHogInit() {
  const $ = _c(6);
  const initRef = useRef(false);
  const consent = usePostHogConsent();
  const consented = consent === "accepted";
  let t0;
  let t1;
  if ($[0] !== consented) {
    t0 = () => {
      if (!POSTHOG_KEY) {
        return;
      }
      if (consented) {
        posthog.opt_in_capturing();
      } else {
        posthog.opt_out_capturing();
        posthog.reset();
      }
    };
    t1 = [consented];
    $[0] = consented;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[3] !== consented) {
    t2 = () => {
      if (!POSTHOG_KEY || !consented || initRef.current) {
        return;
      }
      initRef.current = true;
      applyPostHogDebugQueryParam();
      const posthogConfig = {
        api_host: POSTHOG_BROWSER_PROXY_HOST,
        ui_host: getPostHogAppHost(),
        capture_pageview: false,
        capture_pageleave: true,
        capture_exceptions: true,
        enable_recording_console_log: false,
        property_denylist: POSTHOG_PII_DENYLIST,
        before_send: beforeSendPostHogEvent,
        loaded: _temp
      } satisfies Parameters<typeof posthog.init>[1] & {
        enable_recording_console_log?: boolean;
        property_denylist?: string[];
      };
      posthog.init(POSTHOG_KEY, posthogConfig);
    };
    t3 = [consented];
    $[3] = consented;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  return null;
}
function _temp() {
  window.__posthogInitialized = true;
}
function PostHogPageView() {
  const $ = _c(4);
  const pathname = usePathname();
  const searchParams = useSearchParams();
  let t0;
  let t1;
  if ($[0] !== pathname || $[1] !== searchParams) {
    t0 = () => {
      if (!POSTHOG_KEY || !pathname || !hasPostHogConsent()) {
        return;
      }
      const url = searchParams.size > 0 ? `${pathname}?${searchParams.toString()}` : pathname;
      posthog.capture("$pageview", {
        $current_url: url,
        source: "browser"
      });
    };
    t1 = [pathname, searchParams];
    $[0] = pathname;
    $[1] = searchParams;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  return null;
}
function PostHogIdentify() {
  const $ = _c(4);
  const {
    isSignedIn
  } = useAuth();
  const {
    user
  } = useUser();
  let t0;
  let t1;
  if ($[0] !== isSignedIn || $[1] !== user) {
    t0 = () => {
      if (!POSTHOG_KEY || !hasPostHogConsent()) {
        return;
      }
      if (isSignedIn && user) {
        identifyPosthogUser(user.id);
      } else {
        if (isSignedIn === false) {
          posthog.reset();
        }
      }
    };
    t1 = [isSignedIn, user];
    $[0] = isSignedIn;
    $[1] = user;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  return null;
}
export function PostHogProvider(t0) {
  const $ = _c(7);
  const {
    children
  } = t0;
  if (!POSTHOG_KEY) {
    let t1;
    if ($[0] !== children) {
      t1 = <>{children}</>;
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <PostHogInit />;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <Suspense><PostHogPageView /></Suspense>;
    t3 = <PostHogIdentify />;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  let t4;
  if ($[5] !== children) {
    t4 = <PHProvider client={posthog}>{t1}{t2}{t3}{children}</PHProvider>;
    $[5] = children;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  return t4;
}